/**
 * chart.js
 */

import React from "react";
import { useTheme, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as TooltipChartJS,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2'; 
import { Types } from 'utils/constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  TooltipChartJS,
  ChartDataLabels,
);


const useStyles = makeStyles(theme => ({
  tooltipContainer: {
    flex: 1,
    height: '100%',
    width: '100%',
    padding: `${theme.spacing(1)} 0`,
  },
  chartContainer: {
    height: '100%',
    width: '100%',
    '& canvas': {
      height: '100% !important',
      width: '100% !important'
    }
  }
}));


const Chart = React.memo((props) => {
  const {
    recordGroup,
    data,
    setTooltip,
  } = props;
  const theme = useTheme();
  const trainingSuccessMin = 16;
  const labelSuffix = recordGroup === Types.RecordGroup.TRAINING
    ? 'hours'
    : 'deployments';
  const defaultBarColors = {
    main: theme.palette.sys.surfaceContainerHighest,
    hover: theme.palette.action.hoverBackground(theme.palette.sys.surfaceContainerHighest)
  };
  const successBarColors = {
    main: theme.palette.success.light,
    hover: theme.palette.success.main,
  }
  const backgroundColors = data.map(i => {
    if (recordGroup === Types.RecordGroup.TRAINING) {
      return i.value >= trainingSuccessMin
        ? successBarColors.main
        : defaultBarColors.main;
    } else if (recordGroup === Types.RecordGroup.DEPLOYMENT) {
      return defaultBarColors.main;
    }

    return defaultBarColors.main;
  });
  const hoverColors = data.map(i => {
    if (recordGroup === Types.RecordGroup.TRAINING) {
      return i.value >= trainingSuccessMin
        ? successBarColors.hover
        : defaultBarColors.hover;
    } else if (recordGroup === Types.RecordGroup.DEPLOYMENT) {
      return defaultBarColors.hover;
    }

    return defaultBarColors.hover;
  });


  
  ChartJS.defaults.font.size = 10;
  ChartJS.defaults.color = theme.palette.sys.onSurfaceVariant;


  // bar chart options
  const barChartOptions = {
    layout: {
      padding: {
        top: 0,
        bottom: -8,
        left: 0,
        right: 0
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        formatter: (value, context) => data[context.dataIndex].month.substring(0, 3),
        rotation: -90,
        anchor: 'start',
        align: 'end',
        offset: 0,
      },
      tooltip: {
        enabled: false,
        mode: 'index',
        intersect: false,
        external: (context) => {
          if (context.tooltip.title) {
            setTooltip(context.tooltip.title[0]);
          }
        }
      },
    },
    scales: {
      y: {
        display: false,
        beginAtZero: true,
        min: 0,
        max: Math.max.apply(null, data.map(i => i.value)) || 1,
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          display: false,
        },
      }
    },
  };

  
  // bar chart data
  const barChartData = {
    labels: data.map(i => `${i.month}: ${i.value} ${labelSuffix}`),
    datasets: [{
      barPercentage: 1.1,
      backgroundColor: backgroundColors,
      hoverBackgroundColor: hoverColors,
      minBarLength: 1,
      data: data.map(i => i.value),
    }]
  };


  return (
    <>
      <Bar 
        data={barChartData} 
        options={barChartOptions}
      />
    </>
  );
}, (prev, next) => {
  return (
    prev.data === next.data
  );
});


export default function BarChart(props) {
  const {
    recordGroup,
    data,
  } = props;
  const classes = useStyles();
  const [tooltip, setTooltip] = React.useState();


  return (
    <>
      {data &&
      <div className={classes.tooltipContainer}>
        <Tooltip
          arrow
          placement='top'
          title={tooltip || ''}
        >
          <div className={classes.chartContainer}>
            <Chart 
              recordGroup={recordGroup}
              data={data} 
              setTooltip={setTooltip}
            />
          </div>
        </Tooltip>
      </div>}
    </>
  );
}